import TileLayer from 'ol/layer/Tile';
import TileWmsSource from 'ol/source/TileWMS';
import OsmSource from 'ol/source/OSM';
import VectorTileLayer from 'ol/layer/VectorTile'
import VectorTileSource from 'ol/source/VectorTile'
import MvtFormat from 'ol/format/MVT'
import GeoJsonFormat from 'ol/format/GeoJSON'
import TopoJsonFormat from 'ol/format/TopoJSON'
import KmlFormat from 'ol/format/KML'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import XyzSource from 'ol/source/XYZ'
import { applyStyle } from 'ol-mapbox-style'
import { OlStyleFactory } from './OlStyle'

/**
 * Factory, which creates OpenLayers layer instances according to a given config
 * object.
 */
export const LayerFactory = {

  /**
   * Maps the format literal of the config to the corresponding OL module.
   * @type {Object}
   */
  formatMapping: {
    'MVT': MvtFormat,
    'GeoJSON': GeoJsonFormat,
    'TopoJSON': TopoJsonFormat,
    'KML': KmlFormat
  },

  /**
   * Returns an OpenLayers layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Base} OL layer instance
   */
  async getInstance (lConf, options = {}) {
    // apply LID (Layer ID) if not existent
    if (!lConf.lid) {
      // Make a unique layerId from Layer name and URL so contexts
      // like permalinks can be reapplied.
      lConf.lid = btoa(lConf.url + lConf.name).substr(0, 6);
    }

    // create correct layer type
    if (lConf.type === 'WMS') {
      return this.createWmsLayer(lConf);
    } else if (lConf.type === 'XYZ') {
      return this.createXyzLayer(lConf);
    } else if (lConf.type === 'OSM') {
      return this.createOsmLayer(lConf);
    } else if (lConf.type === 'VECTOR') {
      return this.createVectorLayer(lConf);
    } else if (lConf.type === 'VECTORTILE') {
      return this.createVectorTileLayer(lConf);
    } else if (lConf.type === 'MVTSTYLE') {
      return this.createMvtStyleLayer(lConf);
    } else if (lConf.type === 'LAYERCOLLECTION') {
      return this.createLayersFromCollection(lConf, options);
    } else {
      return null;
    }
  },

  /**
   * Returns an OpenLayers WMS layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Tile} OL WMS layer instance
   */
  createWmsLayer (lConf) {
    const layer = new TileLayer({
      name: lConf.name,
      lid: lConf.lid,
      displayInLayerList: lConf.displayInLayerList,
      selectable: lConf.selectable || false,
      extent: lConf.extent,
      visible: lConf.visible,
      opacity: lConf.opacity,
      source: new TileWmsSource({
        url: lConf.url,
        params: {
          'LAYERS': lConf.layers,
          'TILED': lConf.tiled
        },
        serverType: lConf.serverType,
        attributions: lConf.attributions,
        crossOrigin: 'Anonymous'
      })
    });

    return layer;
  },

  /**
   * Returns an XYZ based tile layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Tile} OL XYZ layer instance
   */
  createXyzLayer (lConf) {
    const xyzLayer = new TileLayer({
      name: lConf.name,
      lid: lConf.lid,
      displayInLayerList: lConf.displayInLayerList,
      selectable: lConf.selectable || false,
      visible: lConf.visible,
      opacity: lConf.opacity,
      source: new XyzSource({
        url: lConf.url,
        crossOrigin: 'Anonymous',
        attributions: lConf.attributions
      })
    });

    return xyzLayer;
  },

  /**
   * Returns an OpenLayers OSM layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Tile} OL OSM layer instance
   */
  createOsmLayer (lConf) {
    const layer = new TileLayer({
      name: lConf.name,
      lid: lConf.lid,
      displayInLayerList: lConf.displayInLayerList,
      selectable: lConf.selectable || false,
      visible: lConf.visible,
      opacity: lConf.opacity,
      source: new OsmSource({ crossOrigin: 'Anonymous' })
    });

    return layer;
  },

  /**
   * Returns an OpenLayers vector layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.Vector} OL vector layer instance
   */
  createVectorLayer (lConf) {
    const vectorLayer = new VectorLayer({
      name: lConf.name,
      lid: lConf.lid,
      tags: lConf.tags,
      displayInLayerList: lConf.displayInLayerList,
      selectable: lConf.selectable || false,
      routable: lConf.routable || false,
      extent: lConf.extent,
      visible: lConf.visible,
      opacity: lConf.opacity,
      source: new VectorSource({
        url: lConf.url,
        format: new this.formatMapping[lConf.format](lConf.formatConfig),
        attributions: lConf.attributions
      }),
      style: OlStyleFactory.getInstance(lConf.style),
      styleSelected: OlStyleFactory.getInstance(lConf.styleSelected),
      hoverable: lConf.hoverable,
      hoverAttribute: lConf.hoverAttribute
    });

    return vectorLayer;
  },

  /**
   * Returns an OpenLayers vector tile layer instance due to given config.
   *
   * @param  {Object} lConf  Layer config object
   * @return {ol.layer.VectorTile} OL vector tile layer instance
   */
  createVectorTileLayer (lConf) {
    const vtLayer = new VectorTileLayer({
      name: lConf.name,
      lid: lConf.lid,
      displayInLayerList: lConf.displayInLayerList,
      selectable: lConf.selectable || false,
      visible: lConf.visible,
      opacity: lConf.opacity,
      source: new VectorTileSource({
        url: lConf.url,
        format: new this.formatMapping[lConf.format](),
        attributions: lConf.attributions
      }),
      style: OlStyleFactory.getInstance(lConf.style),
      hoverable: lConf.hoverable,
      hoverAttribute: lConf.hoverAttribute
    });

    return vtLayer;
  },

  /**
   * Returns a vector tile layer styled by a GL style document (the style
   * specification shared by MapLibre and Mapbox GL), as served by TileServer GL
   * under /styles/<id>/style.json.
   *
   * Unlike VECTORTILE, which expects a Wegue style config, everything here comes
   * out of the style document and the TileJSON it references: source URLs, tile
   * grid, style function and attributions. The layer config therefore only needs
   * a `url` pointing at the style document.
   *
   * @param  {Object} lConf  Layer config object
   * @return {Promise<ol.layer.VectorTile>} OL vector tile layer instance
   */
  async createMvtStyleLayer (lConf) {
    const layer = new VectorTileLayer({
      name: lConf.name,
      lid: lConf.lid,
      displayInLayerList: lConf.displayInLayerList,
      selectable: lConf.selectable || false,
      visible: lConf.visible,
      opacity: lConf.opacity,
      declutter: lConf.declutter !== false,
      renderMode: lConf.renderMode
    });

    // Our tile server's auth proxy already writes the API token into the URLs
    // inside the style document and the TileJSON, so the token in lConf.url is
    // enough. accessToken is only a fallback for servers that do not, and
    // accessTokenParam matches the parameter name the proxy accepts.
    //
    // Deliberately swallowed: a rejection here would travel up through the
    // Promise.all in Map.createLayers and take every other layer with it. An
    // unreachable tile server or an expired token must cost us the basemap, not
    // the whole map. The layer is then left without a source and draws nothing.
    try {
      await applyStyle(layer, lConf.url, lConf.sourceOrLayers || '', {
        accessToken: lConf.accessToken,
        accessTokenParam: lConf.accessTokenParam || 'key'
      });
    } catch (error) {
      console.error(
        'Could not apply the GL style for layer "' + lConf.lid + '" from ' +
        lConf.url + ' - the basemap will stay empty.', error
      );
      return layer;
    }

    // Attributions come from the TileJSON via applyStyle. An explicit config
    // value is an addition to that, not a replacement.
    if (lConf.attributions) {
      const source = layer.getSource();
      const existing = source.getAttributions();
      source.setAttributions(existing
        // getAttributions() hands back an ol Attribution function, which takes
        // the frame state - so forward it rather than calling it bare.
        ? frameState => [].concat(existing(frameState) || [], lConf.attributions)
        : lConf.attributions
      );
    }

    return layer;
  },

  /**
   * Returns an array of Wegue Layer objects from given config.
   *
   * @param  {Object} lConf Wegue Layer list config object
   * @return {Array} array of layer instances
   */
  async createLayersFromCollection (lConf, { locale = '' }) {
    const url = lConf.url.replace(/\{locale\}/, locale);
    const response = await (await fetch(url)).json();
    return Promise.all(response.map(async layerDef => {
      return this.getInstance(layerDef);
    }));
  }
}



// WEBPACK FOOTER //
// ./src/factory/Layer.js